
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import {
  commonModule,
  appointmentsModule,
  actionTypesModule,
  dashboardModule,
  itemsModule,
  companyModule,
  jobsModule,
  installationsModule,
} from "@/store/modules/store-accessor";
import { durationOptionByMinutes, getOptimalTextColor, isNumber, recurrenceTypesOptions, primaryInstallationItemText } from "@/helpers/data";
import AssigneeCombo from "@/components/Combobox/AssigneeCombo.vue";
import AssigneeMultiUserCombo from "@/components/Combobox/AssigneeMultiUserCombo.vue";
import AppointmentCalendar from "@/components/Calendar/AppointmentCalendar.vue";
import EventOverlappingWarning from "@/components/Alerts/EventOverlappingWarning.vue";
import RecurrenceFields from "@/components/Appointments/RecurrenceFields.vue";
import RecurrenceCheckBox from "@/components/Appointments/RecurrenceCheckBox.vue";
import dayjs from "dayjs";
import InstallationUnfinishedCombo from "@/components/Combobox/InstallationUnfinishedCombo.vue";
import InstallationItemWarrantyIndicator from "@/components/InstallationItemWarrantyIndicator.vue";
import { greekSearchFilter } from "@/helpers/data";

import _ from "lodash";

@Component({
  components: {
    AssigneeCombo,
    AssigneeMultiUserCombo,
    InstallationUnfinishedCombo,
    AppointmentCalendar,
    EventOverlappingWarning,
    RecurrenceFields,
    RecurrenceCheckBox,
    InstallationItemWarrantyIndicator,
  },
})
export default class QuickAddAppointment extends Vue {
  @Prop() readonly step: number;
  @Prop() readonly currentStep: any;
  @Prop() readonly installation: any;

  greekSearchFilter = greekSearchFilter;

  date = false;
  time = false;
  addAppointmentLoading = false;
  assigneeLoading = false;
  actionLoading = false;
  isCompleted = false;
  dateMenu = false;
  loadingInstallationPrimaryItems = true;
  recurrenceEndTypeDateMenu = false;
  userChangedDaysOfWeek = false;

  selectUnfinishedJob = false;

  appointmentModel = {
    type_id: { text: "", color: "", cost: 0, id: 0 },
    assignee_id: "",
    assignee_ids: [] as any[],
    appointment_is_confirmed: true,
    appointment_status_id: 2,
    scheduled_start: null,
    scheduled_start_time: "",
    duration: this.durationCombo[1] || "",
    appointment_notes: "",
    installation_id: -1,
    task_id: undefined,
    installation_item_id: this.defaultInstallationItem(),
    description: "",
    send_client_email: false,
    job_id: null,
     // --- Recurrence ---
    is_recurrent: false,
    recurrence_type: null,
    interval: 1,
    days_of_week: null,
    end_type: null,
    occurrence_count: null,
    end_date: null,
    recurrence_diff_jobs: true,
    // --- Recurrence ---
  };

  scheduledDateRule = [
    (val: any) => !!val || this.$i18n.t("scheduled_day_required"),
  ];

  userRule = [(val: any) => !!val || this.$i18n.t("user_required")];

  scheduledTimeRule = [
    (val: any) => !!val || this.$i18n.t("scheduled_time_required"),
  ];

  durationRule = [(val: any) => !!val || this.$i18n.t("duration_required")];

  typeRule = [
    (val: any) => (!!val && val.text != "") || this.$i18n.t("action_required"),
  ];

  installationRule = [
    (val: any) => !!val || this.$i18n.t("installation_required"),
  ];

  notesRule = [
    (val: any) => {
      if (val != null)
        return val.length <= 1000 || this.$i18n.t("notesLengthRule");
      else return true;
    },
  ];

  titleRule = [
    (val: any) => !!val || this.$i18n.t("title_required"),
    (val: any) => val.length <= 300 || this.$i18n.t("form_title_length_rule"),
  ];

  jobDescriptionRule = [
    (val: any) => val.length <= 80 || this.$i18n.t("jobDescriptionLengthRule"),
  ];

  get durationCombo() {
    return commonModule.durationCombo;
  }

  get actionsCombo() {
    return actionTypesModule.actionTypesCombo;
  }

  get installationsCombo() {
    return commonModule.installationsCombo;
  }

  get isAssigneeLoading() {
    return this.assigneeLoading;
  }

  get isActionsLoading() {
    return this.actionLoading;
  }

  get installationPrimaryItems() {
    return itemsModule.installationPrimaryItems;
  }

  primaryItemText(item: Record<string, any>) {
    return primaryInstallationItemText(item);
  }

  get isLoadingInstallationPrimaryItems() {
    return this.loadingInstallationPrimaryItems;
  }

  get disablePrimaryItems() {
    return (
      this.loadingInstallationPrimaryItems ||
      this.installationPrimaryItems.length === 0
    );
  }

  optimalTextColor(color: string) {
    return getOptimalTextColor(color);
  }

  /** Today + current time so the date/time fields match the calendar’s default “today” view and the draft block. */
  prefillDefaultScheduleIfEmpty() {
    if (!this.appointmentModel.scheduled_start) {
      this.appointmentModel.scheduled_start = dayjs().format("YYYY-MM-DD") as any;
    }
    if (!this.appointmentModel.scheduled_start_time) {
      this.appointmentModel.scheduled_start_time = dayjs().format("HH:mm");
    }
  }

  onlyNumbers(evt: any) {
    isNumber(evt);
  }

  setDurationFromActionType(actionType: any) {
    const duration = durationOptionByMinutes(this.durationCombo, actionType?.duration);
    if (duration) {
      (this.appointmentModel as any).duration = duration;
    }
  }

  @Watch("appointmentModel.type_id")
  onActionTypeChange(actionType: any) {
    this.setDurationFromActionType(actionType);
  }

  @Watch("currentStep")
  async stepUpdate() {
    this.appointmentModel.send_client_email = this.clientHasEmail ? this.isSendClientAppointmentEmailEnabled : false;
    if (this.currentStep == this.step) {
      this.prefillDefaultScheduleIfEmpty();
      if (this.installation.id != -1) {
        this.assigneeLoading = true;
        await commonModule.getPersonnelCombo();
        this.assigneeLoading = false;

        await itemsModule.getInstallationPrimaryInventoryItems(
          this.installation.id
        );
        this.loadingInstallationPrimaryItems = false;

        this.actionLoading = true;
        await commonModule.getActionCombo();
        await commonModule.getDurationCombo();
        await actionTypesModule.getActionTypesCombo();
        this.actionLoading = false;
        this.appointmentModel.installation_id = this.installation.id;
        (this.appointmentModel.installation_item_id as any) =
          this.defaultInstallationItem();

          const { defaultOption: defaultRecurrenceType } = recurrenceTypesOptions(this.appointmentModel.interval);
          (this.appointmentModel.recurrence_type as any) = defaultRecurrenceType;

      }
    }
  }

  updateAppointmentAssignee(assignees: any) {
    if (assignees.length > 0) {
      this.appointmentModel.assignee_id = assignees[0];
    }
    this.appointmentModel.assignee_ids = assignees;
  }

  async endStep() {
    if ((this.$refs.form as Vue & { validate: () => boolean }).validate()) {
      this.addAppointmentLoading = true;

      let appointmentModelCopy = _.cloneDeep(this.appointmentModel);

      if (
        typeof this.appointmentModel.assignee_id === "undefined" ||
        this.appointmentModel.assignee_id == "" ||
        this.appointmentModel.assignee_id == null
      ) {
        appointmentModelCopy.assignee_id = "";
      }

      appointmentModelCopy.installation_id = this.installation.id;

      if(this.selectUnfinishedJob) {
        await jobsModule.createAppointmentToJob(appointmentModelCopy);
      } else {
        await appointmentsModule.createAppointment(appointmentModelCopy);
      }

      switch (this.$route.name) {
        case "Dashboard":
          dashboardModule.getDashboardAppointmentsAction(
            dashboardModule.filters
          );
          dashboardModule.getDashboardAppointmentsBarsAction(
            dashboardModule.filters
          );
          break;
        case "Appointments":
          appointmentsModule.getAppointmentsAction(appointmentsModule.filters);
          break;
        case "Calendar":
          appointmentsModule.getCalendarAppointmentsAction(
            appointmentsModule.filters
          );
          break;
      }

      this.addAppointmentLoading = false;
      this.$emit("close-modal");
    }
  }

  defaultInstallationItem() {
    if (
      this.installationPrimaryItems &&
      this.installationPrimaryItems.length === 1
    ) {
      return this.installationPrimaryItems[0];
    }
    return { primary_items: "", id: 0 };
  }

  prevStep() {
    this.selectUnfinishedJob = false;
    this.appointmentModel.installation_id = -1;
    this.$emit("prev-step");
  }

  get jobDescriptionIsEnabled() {
    return (companyModule.company?.preferences as any)?.job_description && !this.selectUnfinishedJob;
  }

  clearTime() {
    this.appointmentModel.scheduled_start_time = "";
  }

  get hasClientSelected() {
    return this.installation && this.installation.client_email !== undefined;
  }

  get clientHasEmail() {
    if (!this.hasClientSelected) return false;
    const email = this.installation.client_email;
    return email !== undefined && email !== null && email !== '';
  }

  get isSendClientAppointmentEmailEnabled() {
    return (companyModule.company?.preferences as Record<string, any>)?.send_client_appointment_email;
  }

  get disableSendEmailToClient() {
    return !(companyModule.company?.preferences as Record<string, any>)?.send_client_appointment_email || !this.hasClientSelected || !this.clientHasEmail;
  }

  updateUnfinishedInstallationJobsSwitch() {
    this.selectUnfinishedJob = !this.selectUnfinishedJob;
    this.appointmentModel.job_id = null;
  }

}
